// Card "Simulações recentes" na home — par com Em destaque na mesma linha.
// Renderiza o histórico de provas/simulações encerradas em formato compacto.
// Quando vazio, mostra placeholder amigável.

import { Award, ChevronRight } from "lucide-react";
import type { StudentHomeResponse } from "./api";

interface Props {
  home: StudentHomeResponse;
  onOpenAll: () => void;
  onOpenBank: (bankId: string) => void;
}

export function SimulacoesRecentesCard({ home, onOpenAll, onOpenBank }: Props) {
  const list = home.provasHistorico;
  const isEmpty = list.length === 0;
  const total = home.totals.provasHistorico;
  return (
    <section class="rounded-md border border-violet-500/40 bg-violet-500/5 p-4">
      <header class="mb-3 flex items-center justify-between gap-2">
        <h2 class="flex items-center gap-2 text-sm font-bold text-violet-700 dark:text-violet-400">
          <Award className="size-4" />
          Simulações recentes
          {list.length > 0 && (
            <span class="inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full bg-violet-500/15 px-1.5 text-[11px] font-bold tabular-nums text-violet-700 ring-1 ring-violet-500/25 dark:text-violet-400">
              {total > list.length ? `${list.length}/${total}` : list.length}
            </span>
          )}
        </h2>
        {list.length > 0 && (
          <button
            type="button"
            onClick={onOpenAll}
            class="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:underline dark:text-violet-400"
          >
            Ver todas <ChevronRight className="size-3.5" />
          </button>
        )}
      </header>
      {isEmpty ? (
        <p class="rounded-md border border-dashed border-violet-500/20 bg-background/40 px-3 py-4 text-center text-[11px] text-muted-foreground">
          Você ainda não encerrou nenhuma simulação.
        </p>
      ) : (
        <ul class="space-y-1.5">
          {list.slice(0, 4).map((p) => {
            const pct = p.percentualAcerto;
            return (
              <li
                key={`${p.bankId}-${p.id ?? p.encerradaEm}`}
                class="flex items-center gap-3 rounded-md border border-border bg-background/60 px-3 py-2 hover:bg-accent/30 cursor-pointer"
                onClick={() => onOpenBank(p.bankId)}
              >
                <div class="min-w-0 flex-1">
                  <div class="truncate text-xs font-semibold">{p.bankName}</div>
                  <div class="mt-0.5 truncate text-[10px] text-muted-foreground">
                    {p.acertos}/{p.total} acertos
                    {p.encerradaEm &&
                      ` · ${formatShort(p.encerradaEm)}`}
                  </div>
                </div>
                {pct != null && (
                  <span
                    class={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-bold tabular-nums ${
                      pct >= 70
                        ? "bg-emerald-500/15 text-emerald-700 ring-1 ring-emerald-500/25 dark:text-emerald-400"
                        : pct >= 50
                        ? "bg-amber-500/15 text-amber-700 ring-1 ring-amber-500/25 dark:text-amber-400"
                        : "bg-rose-500/15 text-rose-700 ring-1 ring-rose-500/25 dark:text-rose-400"
                    }`}
                  >
                    {Math.round(pct)}%
                  </span>
                )}
                <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/60" />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function formatShort(ms: number): string {
  const t = ms;
  const diff = Date.now() - t;
  if (diff < 60_000) return "agora";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d`;
  return new Date(t).toLocaleDateString("pt-BR");
}
